import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    CircularProgress,
    Checkbox
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PersonIcon from "@material-ui/icons/Person";
import PanoramaIcon from "@material-ui/icons/Panorama";
import ImageIcon from "@material-ui/icons/Image";
import agent from "../../../agent";
import CacheHelper from "../../../helper/cache";
import image from "../../../helper/image";
import "./ActImagesModal.css";

@inject("templateStore")
@observer
class ActImagesModal extends Component {
    state = {
        images: [],
        selectedImages: [],
        loading: true,
        downloading: false
    };

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open && this.props.act) {
            this.loadImages();
        }
    }

    loadImages = async () => {
        this.setState({ loading: true, selectedImages: [] });
        
        const { act } = this.props;
        const profileId = act?.id;

        try {
            const response = await agent.Profile.getFullImages(profileId);
            const images = [];

            if (response?.profileImage) {
                const pi = response.profileImage;
                const url = await CacheHelper.getImgLink(pi.urlOriginal || pi.url400 || pi.url200);
                images.push({
                    id: `profile-${pi.id || 'main'}`,
                    type: 'profile',
                    label: 'Profile Image',
                    url: url,
                    downloadKey: pi.urlOriginal || pi.url400 || pi.url200,
                    fileName: pi.fileName,
                    icon: 'person'
                });
            }

            if (response?.backgroundImages && response.backgroundImages.length > 0) {
                for (let i = 0; i < response.backgroundImages.length; i++) {
                    const bg = response.backgroundImages[i];
                    const url = await CacheHelper.getImgLink(bg.urlOriginal || bg.url1920 || bg.url960);
                    images.push({
                        id: `background-${bg.id || i}`,
                        type: 'background',
                        label: response.backgroundImages.length > 1 ? `Background Image ${i + 1}` : 'Background Image',
                        url: url,
                        downloadKey: bg.urlOriginal || bg.url1920 || bg.url960,
                        fileName: bg.fileName,
                        icon: 'panorama'
                    });
                }
            }

            if (response?.galleryImages && response.galleryImages.length > 0) {
                for (let i = 0; i < response.galleryImages.length; i++) {
                    const gi = response.galleryImages[i];
                    const rawKey = gi.urlOriginal || gi.url || gi.storageKey;
                    const url = rawKey ? await CacheHelper.getImgLink(rawKey) : null;
                    const imageTitle = gi.title || gi.fileName;
                    const hiResLabel = imageTitle
                        ? `Hi Res Image ${i + 1} - ${imageTitle}`
                        : `Hi Res Image ${i + 1}`;
                    images.push({
                        id: `gallery-${gi.id || i}`,
                        type: 'gallery',
                        label: hiResLabel,
                        url: url,
                        downloadKey: rawKey,
                        fileName: gi.fileName,
                        icon: 'image'
                    });
                }
            }

            this.setState({ images, loading: false });
        } catch (error) {
            console.error("Error loading images from API:", error);
            const images = [];
            if (act.profileImage || act.profileImageUrl) {
                images.push({
                    id: 'profile',
                    type: 'profile',
                    label: 'Profile Image',
                    url: image.getProfileImage(act),
                    icon: 'person'
                });
            }
            if (act.backgroundImage || act.backgroundImages) {
                const bgImage = act.backgroundImage || (act.backgroundImages && act.backgroundImages[0]);
                if (bgImage) {
                    images.push({
                        id: 'background',
                        type: 'background',
                        label: 'Background Image',
                        url: image.getBackgroundImage(act),
                        icon: 'panorama'
                    });
                }
            }
            this.setState({ images, loading: false });
        }
    };

    toggleImageSelection = (imageId) => {
        const { selectedImages } = this.state;
        if (selectedImages.includes(imageId)) {
            this.setState({ 
                selectedImages: selectedImages.filter(id => id !== imageId) 
            });
        } else {
            this.setState({ 
                selectedImages: [...selectedImages, imageId] 
            });
        }
    };

    handleSelectAll = () => {
        const { images, selectedImages } = this.state;
        if (selectedImages.length === images.length) {
            this.setState({ selectedImages: [] });
        } else {
            this.setState({ selectedImages: images.map(img => img.id) });
        }
    };

    downloadImage = async (img) => {
        try {
            let url = img.url;
            
            if (img.downloadKey) {
                try {
                    url = await CacheHelper.getImgLink(img.downloadKey);
                } catch (err) {
                    console.warn("Could not resolve download URL, using display URL:", err);
                }
            }

            if (!url || !url.startsWith('http')) {
                url = img.url;
            }

            if (!url) {
                throw new Error("No URL available for image");
            }

            try {
                const response = await fetch(url, { mode: 'cors' });
                if (!response.ok) throw new Error('Fetch failed');
                const blob = await response.blob();
                
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                const actName = this.props.act?.actName || 'act';
                const ext = img.fileName ? img.fileName.split('.').pop() : 'jpg';
                link.download = `${actName} - ${img.label}.${ext}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            } catch (fetchErr) {
                console.warn("Direct download failed, opening in new tab:", fetchErr);
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error("Error downloading image:", error);
            throw error;
        }
    };

    handleDownloadSelected = async () => {
        const { images, selectedImages } = this.state;
        
        if (selectedImages.length === 0) {
            this.props.templateStore.openSnackbar("Please select at least one image to download");
            return;
        }

        this.setState({ downloading: true });

        try {
            const selectedImgs = images.filter(img => selectedImages.includes(img.id));
            
            for (const img of selectedImgs) {
                await this.downloadImage(img);
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            
            this.props.templateStore.openSnackbar(`Downloaded ${selectedImgs.length} image(s)`);
        } catch (error) {
            console.error("Error downloading images:", error);
            this.props.templateStore.openSnackbar("Failed to download some images");
        } finally {
            this.setState({ downloading: false });
        }
    };

    getIcon(iconType) {
        switch (iconType) {
            case 'person':
                return <PersonIcon />;
            case 'panorama':
                return <PanoramaIcon />;
            default:
                return <ImageIcon />;
        }
    }

    getGradient(type, index = 0) {
        const gradients = {
            profile: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            gallery: [
                'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)'
            ],
            hires: [
                'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
                'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)'
            ]
        };

        if (type === 'gallery' || type === 'hires') {
            return gradients[type][index % gradients[type].length];
        }
        return gradients[type] || gradients.gallery[0];
    }

    render() {
        const { open, onClose, act } = this.props;
        const { images, selectedImages, loading, downloading } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                className="act-images-modal"
            >
                <DialogTitle disableTypography className="modal-title">
                    <div className="title-content">
                        <PhotoLibraryIcon className="title-icon" />
                        <span>Download Images - {act?.actName || 'Act'}</span>
                    </div>
                    <IconButton onClick={onClose} className="close-btn">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="modal-content">
                    {loading ? (
                        <div className="loading-state">
                            <CircularProgress />
                            <Typography variant="body1">Loading images...</Typography>
                        </div>
                    ) : images.length === 0 ? (
                        <div className="empty-state">
                            <PhotoLibraryIcon className="empty-icon" />
                            <Typography variant="h6">No Images Available</Typography>
                            <Typography variant="body2" color="textSecondary">
                                This act hasn't uploaded any images yet.
                            </Typography>
                        </div>
                    ) : (
                        <>
                            <Typography variant="body2" color="textSecondary" className="instruction-text">
                                Select the images you want to download:
                            </Typography>
                            <div className="images-grid">
                                {images.map((img, index) => (
                                    <div 
                                        key={img.id}
                                        className={`image-card ${selectedImages.includes(img.id) ? 'selected' : ''}`}
                                        onClick={() => this.toggleImageSelection(img.id)}
                                    >
                                        {selectedImages.includes(img.id) && (
                                            <div className="checkbox-overlay">
                                                <CheckCircleIcon />
                                            </div>
                                        )}
                                        <div 
                                            className="image-preview"
                                            style={{ 
                                                backgroundImage: img.url ? `url(${img.url})` : 'none',
                                                background: img.url ? `url(${img.url}) center/cover` : this.getGradient(img.type, index)
                                            }}
                                        >
                                            {!img.url && this.getIcon(img.icon)}
                                        </div>
                                        <div className="image-info">
                                            <Typography variant="subtitle2" className="image-type">
                                                {img.label}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" className="image-size">
                                                {img.type === 'profile' && '1200 x 1200 px'}
                                                {img.type === 'background' && '1920 x 1080 px'}
                                                {(img.type === 'gallery' || img.type === 'hires') && '2400 x 1600 px'}
                                            </Typography>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </DialogContent>

                <DialogActions className="modal-actions">
                    {images.length > 0 && (
                        <>
                            <Typography variant="body2" color="textSecondary" className="selected-count">
                                {selectedImages.length} image{selectedImages.length !== 1 ? 's' : ''} selected
                            </Typography>
                            <Button onClick={this.handleSelectAll}>
                                {selectedImages.length === images.length ? 'Deselect All' : 'Select All'}
                            </Button>
                            <Button
                                onClick={this.handleDownloadSelected}
                                variant="contained"
                                color="primary"
                                disabled={selectedImages.length === 0 || downloading}
                            >
                                {downloading ? (
                                    <>
                                        <CircularProgress size={18} style={{ marginRight: 8, color: '#fff' }} />
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <CloudDownloadIcon style={{ marginRight: 8, fontSize: 18 }} />
                                        Download Selected
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default ActImagesModal;
